import { useMemo } from 'react'
import useDocumentViewerModalViewModel, { RECORD_LOADING_TYPE } from './DocumentViewerModalViewModel'
import { downloadDocumentById } from '@utils'
import cn from 'clsx'

import Dialog from '@components/shared/Dialogs/Dialog'
import DocumentViewer from './DocumentViewer'
import DocumentViewerSearchResultPager from '@components/collection/DocumentViewerV2/DocumentViewerSearchResultPager'
import Pagination from '@components/shared/Pagination'
import { Link } from 'react-router-dom'
import { MdClose, MdDownload, MdEditNote } from 'react-icons/md'
import CircleSpinner from '@components/shared/CircleSpinner'
import Checkbox from '$components/Forms/Checkbox'

const panelProps = {
    // Setting style directly here, bc CSS classes
    // get overridden due to specificity, even with !important
    style: {
        height: 'calc(100vh)',
        maxHeight: 'calc(100vh)',
    },
}

const DocumentViewerModal = ({
    className,
    ...props
}) => {
    
    const viewModel = useDocumentViewerModalViewModel()
    
    //region Memos
    
    const titleView = useMemo(() => viewModel.record && (
        <header className="flex justify-between items-end gap-4">
            <div className="flex flex-col gap-1">
                <div className="flex gap-4 items-baseline">
                    <h4 className="leading-tight">
                        {viewModel.record.name}
                    </h4>
                    <span className="text-sm italic">
                        ({viewModel.pdfPageCount} pages, {viewModel.pageMatches.counts.current} matches on this page)
                    </span>
                </div>
                <span className="text-sm">
                    from collection: {viewModel.record.collection?.name}
                </span>
            </div>
            {viewModel.pageMatches?.counts?.all > 0 && (
                <DocumentViewerSearchResultPager
                    loading={viewModel.loading}
                    pageMatches={viewModel.pageMatches}
                    setPageIndex={viewModel.setPageIndex}
                    prevPageWithMatchesIndex={viewModel.prevPageWithMatchesIndex}
                    nextPageWithMatchesIndex={viewModel.nextPageWithMatchesIndex} />
            )}
        </header>
    ), [
        viewModel.record,
        viewModel.pageMatches,
        viewModel.loading,
        viewModel.pdfPageCount,
        viewModel.setPageIndex,
        viewModel.prevPageWithMatchesIndex,
        viewModel.nextPageWithMatchesIndex,
        viewModel.annotationCompleted,
    ])
    
    const paginationView = useMemo(() => viewModel.record && viewModel.pdfPageCount > 0 && (
        <Pagination
            key="DocumentViewerModal-footer-pagination"
            count={viewModel.pdfPageCount ?? 0}
            itemsPerPage={1}
            pageRangeDisplayed={3}
            forcePage={viewModel.pageIndex}
            setPageIndex={viewModel.setPageIndex}
            onPageChange={e => viewModel.setPageIndex(e.selected)} />
    ), [viewModel.record, viewModel.pdfPageCount, viewModel.pageIndex])
    
    const footerCtasView = useMemo(() => viewModel.record && (
        <ul
            key="DocumentViewerModal-footer-ctas"
            className="w-full grow flex justify-end items-center gap-3 menu menu-horizontal">
            {viewModel.record.collection && (
                <li>
                    <Checkbox
                        labelClassName="!py-0 cursor-pointer text-base"
                        checked={viewModel.annotationCompleted}
                        label="Mark as Done"
                        onChange={() => viewModel.toggleAnnotationCompleted(viewModel.record.id)}/>
                </li>
            )}
            {!!viewModel.record.collection && (
                <li>
                    <button
                        className="btn btn-secondary flex items-center content-center"
                        onClick={() => downloadDocumentById(viewModel.record?.id)}>
                        <MdDownload className="text-xl"/>
                    </button>
                </li>
            )}
            <li>
                <Link
                    className="btn btn-primary flex items-center content-center"
                    type="button"
                    onClick={() => viewModel.setRecord(null)}
                    to={`/edit/${viewModel.record.id}`}
                    disabled={!viewModel.record.pages?.length}>
                    <MdEditNote className="text-xl" />
                    Edit Document
                </Link>
            </li>
        </ul>
    ), [viewModel.record, viewModel.annotationCompleted])
    
    //endregion Memos
    
    if (!viewModel.record?.id) return null
    
    return (
        
        <Dialog
            className={cn('DocumentViewerModal relative', className)}
            panelClassName="w-auto h-screen min-w-[min(1280px,100vw)] max-w-screen-xl scale-y-95 modal-content"
            panelProps={panelProps}
            open={viewModel.open}
            setOpen={value => {
                if (!value) {
                    viewModel.setRecord(null)
                    viewModel.handleClose()
                }
            }}
            modal={false}
            title={titleView}
            subtitle={null}
            positiveText={<>
                <MdClose className="text-xl" />
                Close
            </>}
            footers={[
                paginationView,
                footerCtasView,
            ]}>
            
            {viewModel.recordLoading !== RECORD_LOADING_TYPE.LOADED ? (
                <div>
                    <CircleSpinner className="absolute-centered" />
                </div>
            ) : (
                <DocumentViewer
                    className="p-2"
                    record={viewModel.record}
                    pageIndex={viewModel.pageIndex}
                    onHaveMatchesByPageIndex={viewModel.updateMatches}
                    {...props} />
            )}
        
        </Dialog>
        
    )
    
}

export default DocumentViewerModal
